<template>
  <div class="space-y-6">
    <TextParagraph>
      {{ t("description") }}
    </TextParagraph>
    <SelectMenuLabel
      :selected="selectedReason"
      :data-items="reasons"
      :label="t('reasonLabel')"
      :rem-height="17"
      @update:selected="(item) => selectReason(item as Reason)"
    />

    <div v-show="selectedReason.data?.showComment">
      <FormTextArea
        id="vue-plan-cancel-comments-textarea"
        :model-value="reasonComment"
        :placeholder="t(`reasons.${props.selectedReason.key}.commentPlaceholder`)"
        :label="t(`reasons.${props.selectedReason.key}.commentLabel`)"
        :rows="3"
        :maxlength="200"
        :error="showCommentError ? t('commentError') : undefined"
        @update:model-value="updateReasonComment"
      />
    </div>
    <AlertBox v-show="selectedReason.data?.showWarning" theme="warning">
      <span class="font-medium">{{ t(`reasons.${props.selectedReason.key}.contactSupportDescription`) }}</span>
    </AlertBox>
    <AlertBox v-show="selectedReason.data?.showContactSupport" theme="info">
      <template #icon>
        <ChatAltIcon />
      </template>
      <span class="font-medium">
        {{ t(`reasons.${props.selectedReason.key}.contactSupportText`) }}
      </span>
      <template #actions>
        <SimpleButton theme="primary-clean" class="-ml-4" @click="contactIntercom"
          >{{ t("buttonContact") }}
          <template #trailing>
            <ArrowSmRightIcon />
          </template>
        </SimpleButton>
      </template>
    </AlertBox>
    <div class="flex justify-between">
      <SimpleButton theme="white" @click="close">{{ t("buttonClose") }}</SimpleButton>
      <SimpleButton theme="danger" :disabled="!allowContinue || showCommentError || loadingContinue" :loading="loadingContinue" @click="onContinue"
        >{{ t("buttonContinue") }}<template #trailing><ChevronRightIcon /> </template>
      </SimpleButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watchEffect } from "vue";

import type { Reason } from "../planCancellation.types";
import { getReasons } from "../reasons";

// Components
import SelectMenuLabel from "@molecules/SelectMenuLabel.vue";
import FormTextArea from "@molecules/FormTextArea.vue";
import TextParagraph from "@atoms/TextParagraph.vue";
import SimpleButton from "@atoms/SimpleButton.vue";
import AlertBox from "@atoms/AlertBox.vue";

// Icon
import { ChatAltIcon, ChevronRightIcon } from "@heroicons/vue/solid";
import { ArrowSmRightIcon } from "@heroicons/vue/outline";

// Utils
import Intercom from "@helpers/intercom";

// I18n
import { useI18n } from "vue-i18n";

const { t } = useI18n();

//Props
const props = withDefaults(
  defineProps<{
    selectedReason: Reason;
    reasonComment: string;
    loadingContinue?: boolean;
  }>(),
  {
    loadingContinue: false,
  },
);

//Emits
const emit = defineEmits<{
  (e: "update:selectedReason", value: Reason): void;
  (e: "update:reasonComment", value: string): void;
  (e: "continue"): void;
  (e: "close"): void;
}>();

const close = () => {
  emit("close");
};

const selectReason = (reason: Reason) => {
  emit("update:selectedReason", reason);
};

const reasons = getReasons();

const showCommentError = ref(false);
const allowContinue = ref<boolean>(false);
const onContinue = () => {
  if (props.selectedReason.data?.showComment && props.reasonComment.length === 0) {
    showCommentError.value = true;
    return;
  }
  emit("continue");
};

const contactIntercom = () => {
  Intercom.showNewMessage(t(`reasons.${props.selectedReason.key}.intercomMessage`));
  close();
};

const updateReasonComment = (comment: string) => {
  showCommentError.value = false;
  emit("update:reasonComment", comment);
};

watchEffect(() => {
  allowContinue.value = !props.selectedReason.data || props.selectedReason.data.validOption;
  showCommentError.value = false;
});
</script>

<i18n lang="jsonc">
{
  "es": {
    "description": "Lamentamos que quieras cancelar tu suscripción. Valoramos mucho tus comentarios y sugerencias para seguir mejorando nuestro producto 🙂",
    "reasonLabel": "Motivo de cancelación",
    "reasons": {
      "too_expensive": {
        "label": "Precio (Me resulta costoso)",
        "intercomMessage": "Hola! Quisiera encontrar alguna forma de reducir mis costos para poder seguir usando Perfit.",
        "contactSupportText": "¿Te gustaría hablar con un representante para analizar cómo reducir los costos?",
      },
      "bad_results": {
        "label": "No tuve los resultados esperados",
        "commentLabel": "¿Cuáles eran tus expectativas?",
        "commentPlaceholder": "Cuéntanos un poco más. Tus comentarios nos ayudan a seguir mejorando.",
        "intercomMessage": "Hola! Quisiera recibir asesoramiento para intentar mejorar los resultados.",
        "contactSupportText": "¿Quieres hablar con un representante para entender cómo mejorar tus resultados?",
      },
      "missing_features": {
        "label": "Falta de funcionalidades o integración",
        "commentLabel": "¿Cuáles son esas funcionalidades?",
        "commentPlaceholder": "Cuéntanos un poco más. Tus comentarios nos ayudan a seguir mejorando.",
      },
      "too_busy": {
        "label": "Falta de equipo o tiempo para usar la herramienta",
        "intercomMessage": "Hola! Quisiera recibir más información sobre los servicios de gestión de cuenta.",
        "contactSupportText": "Contamos con especialistas que pueden ayudarte a implementar estrategias y gestionar tu cuenta. ¿Te interesa recibir más información?",
      },
      "bad_experience": {
        "label": "Inestabilidad en la plataforma o servicio / Me resulta complejo usar la plataforma",
        "commentLabel": "¿Alguna situación en particular para mencionar?",
        "commentPlaceholder": "Cuéntanos un poco más. Tus comentarios nos ayudan a seguir mejorando.",
      },
      "business_close": {
        "label": "Cierra la empresa",
      },
      "defaulter": {
        "label": "Moroso incontactable (Master User)",
        "commentLabel": "Comentarios",
        "commentPlaceholder": "Detalle de la baja",
      },
      "fraud": {
        "label": "Baja por fraude/uso indebido/Contracargo de pagos (Master User)",
        "commentLabel": "Comentarios",
        "commentPlaceholder": "Detalle de la baja",
      },
      "account_change": {
        "label": "Cambio de plan/Tengo otra cuenta en Perfit que seguiré usando (Master User)",
        "commentLabel": "Comentarios",
        "commentPlaceholder": "Detalle de la baja",
      },
      "seasonality": {
        "label": "Estacionalidad del rubro (Master User)",
        "commentLabel": "Comentarios",
        "commentPlaceholder": "Detalle de la baja",
      },
      "tiendanube_close": {
        "label": "Cierro mi Tiendanube (Master User)",
      },
      "other": {
        "label": "Otra razón",
        "commentLabel": "Comentarios",
        "commentPlaceholder": "Detalle de la baja",
      },
      "audited": {
        "label": "Deshabilitada por auditoría (Master User)",
        "commentLabel": "Comentarios",
        "commentPlaceholder": "Detalle de la baja",
      },
    },
    "commentError": "Por favor completa estos detalles para ayudarnos a mejorar.",
    "buttonContinue": "Siguiente",
    "buttonClose": "Mejor no",
    "buttonContact": "Sí, quiero charlar",
  },
  "pt": {
    "description": "Lamentamos que você queira cancelar sua assinatura. Valorizamos muito seus comentários e sugestões para continuarmos melhorando nosso produto 🙂",
    "reasonLabel": "Motivo do cancelamento",
    "reasons": {
      "too_expensive": {
        "label": "Preço (É caro para mim)",
        "intercomMessage": "Oi! Gostaria de encontrar uma forma de reduzir meus custos para poder continuar usando a Perfit.",
        "contactSupportText": "Gostaria de falar com um representante para discutir como reduzir os custos?",
      },
      "bad_results": {
        "label": "Não obtive o resultado esperado",
        "commentLabel": "Quais eram suas expectativas?",
        "commentPlaceholder": "Conte-nos um pouco mais. Seus comentários nos ajudam a continuar melhorando.",
        "intercomMessage": "Oi! Gostaria de receber orientação para tentar melhorar os resultados.",
        "contactSupportText": "Deseja falar com um representante para entender como melhorar seus resultados?",
      },
      "missing_features": {
        "label": "Falta de funcionalidades ou Integrações",
        "commentLabel": "Quais são essas funcionalidades?",
        "commentPlaceholder": "Conte-nos um pouco mais. Seus comentários nos ajudam a continuar melhorando.",
      },
      "too_busy": {
        "label": "Falta de equipe ou tempo para utilizar a plataforma",
        "intercomMessage": "Oi! Gostaria de receber mais informações sobre os serviços de gerenciamento de conta.",
        "contactSupportText": "Contamos com especialistas que podem ajudá-lo a implementar estratégias e gerenciar sua conta. Você gostaria de receber mais informações?",
      },
      "bad_experience": {
        "label": "Instabilidade na plataforma ou no serviço / Acho a plataforma difícil de usar",
        "commentLabel": "Alguma situação específica para mencionar?",
        "commentPlaceholder": "Conte-nos um pouco mais. Seus comentários nos ajudam a continuar melhorando.",
      },
      "business_close": {
        "label": "Estou encerrando minha empresa",
      },
      "defaulter": {
        "label": "Inadimplente e incontactável (Master User)",
        "commentLabel": "Comentários",
        "commentPlaceholder": "Detalhes do cancelamento",
      },
      "fraud": {
        "label": "Baixa por fraude/uso indevido/Chargeback de pagamentos (Master User)",
        "commentLabel": "Comentários",
        "commentPlaceholder": "Detalhes do cancelamento",
      },
      "account_change": {
        "label": "Mudança de plano/Tenho outra conta na Perfit que continuarei usando (Master User)",
        "commentLabel": "Comentários",
        "commentPlaceholder": "Detalhes do cancelamento",
      },
      "seasonality": {
        "label": "Sazonalidade do setor (Master User)",
        "commentLabel": "Comentários",
        "commentPlaceholder": "Detalhes do cancelamento",
      },
      "tiendanube_close": {
        "label": "Estou encerrando minha Tiendanube (Master User)",
      },
      "other": {
        "label": "Outro motivo",
        "commentLabel": "Comentários",
        "commentPlaceholder": "Detalhes do cancelamento",
      },
      "audited": {
        "label": "Desabilitado por auditoria (Master)",
        "commentLabel": "Comentários",
        "commentPlaceholder": "Detalhes do cancelamento",
      },
    },
    "commentError": "Por favor, preencha esses detalhes para nos ajudar a melhorar.",
    "buttonContinue": "Continuar",
    "buttonClose": "Melhor não",
    "buttonContact": "Sim, quero conversar",
  },
}
</i18n>
